import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { DashboardProvider, useDashboard } from './contexts/DashboardContext';
import { DashboardReadApi } from './api';
import { DashboardGrid } from './DashboardGrid/DashboardGrid';
import { WidgetLibrary } from './WidgetLibrary/WidgetLibrary';
import { WidgetEditor } from './WidgetEditor/WidgetEditor';
import { FilterBar } from './FilterBar/FilterBar';
import { FilterEditor } from './FilterEditor/FilterEditor';
import { ShareDashboardModal } from './ShareDashboardModal/ShareDashboardModal';
import { LoaderSmall } from '../LoaderSmall/LoaderSmall';
import Tooltip from '../Tooltip/Tooltip';
import { ReactComponent as ShareIcon } from '../../pages/ReportPage/assets/share.svg';
import styles from './CustomDashboard.module.scss';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import './wf-select-portal.scss';

const FILTER_PARAM_PREFIX = 'f_';

/** Parse URL search params into filter values. */
function parseFilterParams(searchParams: URLSearchParams): Record<string, any> {
  const values: Record<string, any> = {};
  searchParams.forEach((val, key) => {
    if (!key.startsWith(FILTER_PARAM_PREFIX)) return;
    const paramName = key.slice(FILTER_PARAM_PREFIX.length);
    if (!paramName) return;

    // Try to parse JSON values (arrays, objects like date_range {from, to})
    try {
      const parsed = JSON.parse(val);
      values[paramName] = parsed;
    } catch {
      values[paramName] = val;
    }
  });
  return values;
}

/**
 * Serialize filter values to URL search params. Non-filter params already in
 * the URL (e.g. the share link's `h` / `with_password`) are kept as they are.
 */
function serializeFilterParams(
  filterValues: Record<string, any>,
  current: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams();
  current.forEach((val, key) => {
    if (!key.startsWith(FILTER_PARAM_PREFIX)) params.set(key, val);
  });
  Object.entries(filterValues).forEach(([key, val]) => {
    if (val === undefined || val === null) return;
    const serialized = typeof val === 'object' ? JSON.stringify(val) : String(val);
    params.set(`${FILTER_PARAM_PREFIX}${key}`, serialized);
  });
  return params;
}

interface CustomDashboardInnerProps {
  dashboardId: string;
  isAdmin: boolean;
  // embedded (artifact tab): no page chrome and no URL filter sync — the host
  // page owns the URL
  embedded?: boolean;
  // public viewer behind a share link: title, filters and widgets only — no
  // breadcrumb into the app, no toolbar, no share button
  isShare?: boolean;
}

const CustomDashboardInner: React.FC<CustomDashboardInnerProps> = ({
  dashboardId,
  isAdmin,
  embedded = false,
  isShare = false,
}) => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    activeDashboard,
    displayedDashboard,
    isLoading,
    isEditing,
    isSaving,
    selectedWidgetId,
    filterValues,
    filterOptions,
    selectDashboard,
    selectWidget,
    startEditing,
    saveDashboard,
    cancelEditing,
    executeWidgetQuery,
    setFilterValue,
    clearFilterValues,
    addFilter,
    updateFilter: updateFilterInDraft,
    removeFilter,
    saveDraftFilters,
    reloadEditingDashboard,
    loadFilterOptions,
  } = useDashboard();

  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isFilterEditorOpen, setIsFilterEditorOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const initializedRef = useRef(false);


  // Capture initial URL params once on mount (before any effects clear them)
  const initialUrlParamsRef = useRef<Record<string, any> | null>(null);
  if (initialUrlParamsRef.current === null) {
    initialUrlParamsRef.current = embedded ? {} : parseFilterParams(searchParams);
  }

  useEffect(() => {
    // Pass URL filter params to selectDashboard so they're applied atomically with defaults
    const urlParams = initialUrlParamsRef.current ?? {};
    const hasUrlParams = Object.keys(urlParams).length > 0;
    selectDashboard(dashboardId, hasUrlParams ? urlParams : undefined);
    initializedRef.current = false;
  }, [dashboardId, selectDashboard]);

  // Mark as initialized once dashboard is loaded
  useEffect(() => {
    if (activeDashboard && !isLoading) {
      initializedRef.current = true;
    }
  }, [activeDashboard, isLoading]);

  // Sync filter values to URL (only after initialization)
  const filterValuesKey = JSON.stringify(filterValues);
  const prevFilterValuesRef = useRef(filterValuesKey);

  useEffect(() => {
    if (embedded || !initializedRef.current) return;
    // Exclude hidden filter values from URL
    const hiddenParams = new Set(
      (displayedDashboard?.filters ?? [])
        .filter((f) => f.hidden)
        .map((f) => f.parameterName)
    );
    const visibleFilterValues: Record<string, any> = {};
    Object.entries(filterValues).forEach(([key, val]) => {
      if (!hiddenParams.has(key)) {
        visibleFilterValues[key] = val;
      }
    });
    setSearchParams((prev) => serializeFilterParams(visibleFilterValues, prev), { replace: true });
  }, [embedded, filterValuesKey, setSearchParams, filterValues, displayedDashboard?.filters]);

  const selectedWidget = displayedDashboard?.widgets.find(
    (w) => w.id === selectedWidgetId
  );

  // Keep prevFilterValuesRef in sync (used by URL sync effect)
  useEffect(() => {
    prevFilterValuesRef.current = filterValuesKey;
  }, [filterValuesKey]);

  const handleSave = useCallback(async () => {
    const confirmed = window.confirm(
      t('Before saving, please verify that the dashboard view is properly restricted to the intended audience. Ensure all data access filters (especially hidden ones) are correctly configured to prevent unauthorized data exposure.')
    );
    if (!confirmed) return;
    await saveDashboard();
  }, [saveDashboard, t]);

  const handleFilterChange = useCallback(
    (parameterName: string, value: any) => {
      setFilterValue(parameterName, value);
    },
    [setFilterValue]
  );

  const handleClearFilters = useCallback(() => {
    clearFilterValues();
  }, [clearFilterValues]);

  if (isLoading) {
    const loader = (
      <div className={styles.loaderWrapper}>
        <LoaderSmall />
      </div>
    );
    return embedded ? loader : <div className="list-content">{loader}</div>;
  }

  if (!activeDashboard && (embedded || isShare)) {
    const emptyState = (
      <div className={styles.emptyState}>
        <p>{t('Dashboard not found')}</p>
      </div>
    );
    return embedded ? emptyState : <div className="list-content">{emptyState}</div>;
  }

  if (!activeDashboard) {
    return (
      <div className="list-content">
        <div className="page-header">
          <div className="breadcrumb">
            <Link to="/custom-dashboards">{t('Dashboards')}</Link>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>{t('Dashboard not found')}</p>
        </div>
      </div>
    );
  }

  if (embedded) {
    return (
      <div className={styles.container}>
        {displayedDashboard && displayedDashboard.filters.length > 0 && (
          <FilterBar
            filters={displayedDashboard.filters}
            filterOptions={filterOptions}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearFilters}
          />
        )}
        <DashboardGrid onAddWidget={() => undefined} />
      </div>
    );
  }

  if (isShare) {
    return (
      <div className="list-content">
        <div className={styles.container}>
          <h1 className={styles.dashboardName}>{activeDashboard.title}</h1>

          {displayedDashboard && displayedDashboard.filters.length > 0 && (
            <FilterBar
              filters={displayedDashboard.filters}
              filterOptions={filterOptions}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onClearAll={handleClearFilters}
            />
          )}

          <DashboardGrid onAddWidget={() => undefined} />
        </div>
      </div>
    );
  }

  return (
    <div className="list-content">
      <div className={styles.container}>
        <div className="page-header">
          <div className="breadcrumb">
            <span><Link to="/custom-dashboards">{t('Dashboards')}</Link></span>
            <span>{activeDashboard.title}</span>
          </div>
        </div>
        <div className={styles.toolbar}>
          {isAdmin && (
            isEditing ? (
              <>
                <button
                  className="new-button"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? t('Saving...') : t('Save')}
                </button>
                <button
                  className="btn-reset"
                  onClick={cancelEditing}
                  disabled={isSaving}
                >
                  {t('Cancel')}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setIsLibraryOpen(true)}
                  disabled={isSaving}
                >
                  + {t('Add Widget')}
                </button>
                <button
                  className="btn-primary"
                  onClick={() => setIsFilterEditorOpen(true)}
                  disabled={isSaving}
                >
                  {t('Manage Filters')}
                </button>
              </>
            ) : (
              <button
                className="new-button"
                onClick={startEditing}
              >
                {t('Edit Dashboard')}
              </button>
            )
          )}
          {!isEditing && (
            <div className={styles.shareAction}>
              <Tooltip content={t('Share')} position="bottom">
                <button
                  type="button"
                  className="new-button"
                  aria-label={t('Share')}
                  onClick={() => setIsShareModalOpen(true)}
                >
                  <ShareIcon aria-hidden="true" focusable="false" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>

        <h1 className={styles.dashboardName}>{activeDashboard.title}</h1>

        {displayedDashboard && displayedDashboard.filters.length > 0 && (
          <FilterBar
            filters={displayedDashboard.filters}
            filterOptions={filterOptions}
            filterValues={filterValues}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearFilters}
          />
        )}

        <DashboardGrid onAddWidget={() => setIsLibraryOpen(true)} />

        <WidgetLibrary
          isOpen={isLibraryOpen && isEditing}
          onClose={() => setIsLibraryOpen(false)}
        />

        <ShareDashboardModal
          isVisible={isShareModalOpen}
          dashboardId={activeDashboard.id}
          onClose={() => setIsShareModalOpen(false)}
        />

        {selectedWidget && (
          <WidgetEditor
            widget={selectedWidget}
            onClose={() => selectWidget(null)}
          />
        )}

        {isFilterEditorOpen && displayedDashboard && (
          <FilterEditor
            filters={displayedDashboard.filters}
            widgets={displayedDashboard.widgets}
            dashboardId={displayedDashboard.id}
            filterValues={filterValues}
            onAdd={addFilter}
            onUpdate={updateFilterInDraft}
            onRemove={removeFilter}
            onClose={() => setIsFilterEditorOpen(false)}
            onSaveDraftFilters={saveDraftFilters}
            onLoadFilterOptions={loadFilterOptions}
            onReloadDashboard={reloadEditingDashboard}
          />
        )}
      </div>
    </div>
  );
};

interface CustomDashboardProps {
  dashboardId: string;
  isAdmin: boolean;
  embedded?: boolean;
  isShare?: boolean;
  api?: Partial<DashboardReadApi>;
}

export const CustomDashboard: React.FC<CustomDashboardProps> = ({
  dashboardId,
  isAdmin,
  embedded,
  isShare,
  api,
}) => {
  return (
    <DashboardProvider isAdmin={isAdmin} api={api}>
      <CustomDashboardInner
        dashboardId={dashboardId}
        isAdmin={isAdmin}
        embedded={embedded}
        isShare={isShare}
      />
    </DashboardProvider>
  );
};
